import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';
import { useActivityLogPreview } from 'hooks/useActivityLogPreview';
import { getTenantDetails, getValueFromCookies, removeValueFromCookies, setAppTitle } from 'utils/utils';
import { clearHeicCache } from 'utils/heicUtils';

/**
 * Encapsulates all business logic for PreviewModal:
 * - Activity logging (decoupled from getFiles)
 * - getFiles refresh (called once on close only)
 * - URL param management (file_id)
 * - App title management
 * - Auto time-entry: proper entry/exit per file, case-specific, skipped for firm documents
 */
export function usePreviewModal() {
  const {
    modals,
    previewList,
    selectedPreviewItem,
    setSelectedPreviewItem,
    versionFileUpload,
    onMountUnMount,
    onOperationBreak,
    isClientFileManager,
    isGlobalFileManager,
    isFirmDocuments,
    fileManagerFilters,
    caseDetails,
    query,
    getFiles,
    currentView,
    pageLimit,
  } = useFileManagerContext();

  const { showPreview, setShowPreview } = modals;
  const { addActivity } = useActivityLogPreview();
  const location = useLocation();
  const history = useHistory();

  // Stable refs for cleanup — avoids stale closure in unmount
  const latestOnMountUnMountRef = useRef(onMountUnMount);
  const latestOnOperationBreakRef = useRef(onOperationBreak);
  useEffect(() => {
    latestOnMountUnMountRef.current = onMountUnMount;
  }, [onMountUnMount]);
  useEffect(() => {
    latestOnOperationBreakRef.current = onOperationBreak;
  }, [onOperationBreak]);

  // Tracks the case_id and case_name of the currently open time entry (null = none active)
  const activeCaseIdRef = useRef(null);
  const activeCaseNameRef = useRef(null);

  // App tab title
  useEffect(() => {
    setAppTitle('File Manager', selectedPreviewItem?.display_name || '');
    return () => setAppTitle('File Manager');
  }, [selectedPreviewItem]);

  // Free converted HEIC blob URLs when the modal closes
  useEffect(() => {
    return () => clearHeicCache();
  }, []);

  // Unmount cleanup — end whichever time entry is currently active, then break
  useEffect(() => {
    return () => {
      const caseId = activeCaseIdRef.current;
      if (caseId) {
        latestOnMountUnMountRef.current?.('file-preview', 'unmounted', caseId, activeCaseNameRef.current);
        activeCaseIdRef.current = null;
        activeCaseNameRef.current = null;
      }
      setTimeout(() => {
        latestOnOperationBreakRef.current?.();
      }, 3000);
    };
  }, []);

  /**
   * Handles time entry entry/exit when the active file changes.
   * - Skipped entirely for firm documents and lead files.
   * - Ends the previous case entry before starting a new one when the case changes.
   * - Does nothing when navigating to another file in the same case.
   */
  const handleFileTimeEntry = useCallback(
    (item) => {
      if (isFirmDocuments || item?.is_lead) {
        // If we were tracking a case entry, end it now (e.g. navigated to a lead file)
        const prev = activeCaseIdRef.current;
        if (prev) {
          typeof onMountUnMount === 'function' && onMountUnMount('file-preview', 'unmounted', prev, activeCaseNameRef.current);
          activeCaseIdRef.current = null;
          activeCaseNameRef.current = null;
        }
        return;
      }

      const newCaseId = fileManagerFilters?.case?.id || caseDetails?.case_id || item?.case_id;
      if (!newCaseId) return;

      const newCaseName = item?.case_name;
      const prev = activeCaseIdRef.current;

      if (prev && prev !== newCaseId) {
        // Switching to a different case — end the previous entry first
        typeof onMountUnMount === 'function' && onMountUnMount('file-preview', 'unmounted', prev, activeCaseNameRef.current);
      }

      if (prev !== newCaseId) {
        // New case or first file in session — start a new entry
        typeof onMountUnMount === 'function' && onMountUnMount('file-preview', 'mounted', newCaseId, newCaseName);
        activeCaseIdRef.current = newCaseId;
        activeCaseNameRef.current = newCaseName;
      }
      // Same case as before — entry continues, no action needed
    },
    [isFirmDocuments, fileManagerFilters, caseDetails, onMountUnMount]
  );

  /**
   * Builds the payload object for getFiles API calls.
   * Single source of truth — was duplicated in two places in V1.
   */
  const buildGetFilesObj = useCallback(() => {
    const obj = {
      case_id: isGlobalFileManager || isClientFileManager ? fileManagerFilters?.case?.id || '' : caseDetails?.case_id,
      selectedFolder: fileManagerFilters?.selectedFolder,
      selectedType: fileManagerFilters?.fileType,
      tag: fileManagerFilters?.tag,
      query,
      folder_id: fileManagerFilters?.selectedFolder,
      is_read: isGlobalFileManager ? fileManagerFilters?.is_read : fileManagerFilters?.fileStatus,
    };

    if (isClientFileManager) {
      if (fileManagerFilters?.shared_folder) {
        obj.shared_folder = fileManagerFilters.shared_folder;
      }
      if (
        fileManagerFilters?.shared_contact_list &&
        Array.isArray(fileManagerFilters.shared_contact_list) &&
        fileManagerFilters.shared_contact_list.length > 0
      ) {
        obj.shared_contact_list = fileManagerFilters.shared_contact_list;
      }
      if (fileManagerFilters?.folder_shared === true) {
        obj.folder_shared = fileManagerFilters.folder_shared;
      }
    }

    return obj;
  }, [isGlobalFileManager, isClientFileManager, fileManagerFilters, caseDetails?.case_id, query]);

  const removeQueryParam = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('file_id');
    history.push({ pathname: location.pathname, search: searchParams.toString() });
    if (getValueFromCookies('redirect_url')) {
      removeValueFromCookies('redirect_url');
    }
  }, [location, history]);

  const handleSlideChange = useCallback(
    (item) => {
      const fileId = item?.object_id;
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('file_id', fileId);
      history.push({ pathname: location.pathname, search: searchParams.toString() });
    },
    [location, history]
  );

  /**
   * Logs a document-read activity event and triggers the time entry for this file.
   * Deliberately decoupled from getFiles — refreshing the file list
   * on every file view was causing excessive API traffic in V1.
   */
  const createActivityLog = useCallback(
    (item) => {
      setTimeout(() => {
        const [tenantId] = getTenantDetails();
        const stateDetails = location?.state?.case_details;
        const data = {
          event_key: 'Read',
          event_type: 'document',
          event_subtype: 'read_document',
          message: `Viewed Document: ${item?.display_name ?? stateDetails?.display_name}`,
          event_status: 'success',
          pk: item?.pk ?? stateDetails?.pk,
          sk: item?.sk ?? stateDetails?.sk,
          event_items: {
            case_id: item?.case_id ?? stateDetails?.case_id ?? '',
            tenant_id: tenantId,
            case_name: item?.case_name ?? stateDetails?.case_name ?? '',
            is_lead: item?.is_lead ?? stateDetails?.is_lead,
          },
        };

        addActivity(data).catch((err) => {
          console.error('Error posting activity log:', err);
        });
      }, 1000);

      handleFileTimeEntry(item);
    },
    [addActivity, location?.state, handleFileTimeEntry]
  );

  const closePreviewModal = useCallback(() => {
    removeQueryParam();
    setShowPreview(false);
    setSelectedPreviewItem();
    // Single getFiles call — only on modal close, not on every file view
    getFiles(buildGetFilesObj(), currentView, pageLimit);
  }, [removeQueryParam, setShowPreview, setSelectedPreviewItem, getFiles, buildGetFilesObj, currentView, pageLimit]);

  return {
    showPreview,
    selectedPreviewItem,
    setSelectedPreviewItem,
    previewList,
    versionFileUpload,
    isClientFileManager,
    createActivityLog,
    handleSlideChange,
    closePreviewModal,
  };
}
